import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useUrlState, safeInternalPath } from "@/hooks/useUrlState";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { TestBadge, IntentBadge, EmptyState, ErrorState, ListSkeleton, WhatsAppIcon, VoiceStateBadge } from "@/components/common";
import { QuickWhatsAppDialog } from "@/components/QuickComms";
import { TagsEditor } from "@/components/TagsEditor";
import { OnboardingWizardDialog } from "@/components/OnboardingWizard";
import { maybeShowTaskReview, notifyTaskReviewChanged } from "@/lib/taskReview";
import { PhoneCall, UserSearch, CalendarClock } from "lucide-react";
import { useSlotPicker } from "@/hooks/useSlotPicker";
import { SlotPickerView } from "@/components/SlotPickerView";

// 2026-09 P3-C — these are all Assigned-Leads workload queues, keyed on
// lead/opportunity RESPONSIBILITY (owner_user_id), never on who actually
// placed a call. Deliberately separate from the Call Activity view below
// (individual call attempts, keyed on the real caller) — see CallsPage's
// own view toggle.
const QUEUES = [
  { key: "my", label: "My Assigned Leads" },
  { key: "new_leads", label: "New / To Call" },
  { key: "followups_today", label: "Follow-ups Today" },
  { key: "connected_followups", label: "Connected" },
  { key: "assigned_not_connected", label: "Assigned Leads — Not Connected" },
  { key: "human_followup", label: "Human Follow-up Required" },
  { key: "team", label: "Team Queue" },
  { key: "lost", label: "Lost" },
];

// Queues where the backend honours scope=team/company + owner_user_id
// (2026-09 Phase 2 — reuses the exact reports.team/reports.company gate
// call_queues already enforces; see backend SCOPE_ELIGIBLE_QUEUES).
const SCOPE_ELIGIBLE_QUEUES = ["connected_followups", "assigned_not_connected", "human_followup"];

export const CallLogDialog = ({ row, open, setOpen, onLogged }) => {
  const { user, hasCap } = useAuth();
  // 2026-09 P4 Round 2 — when this dialog was reached by launching an
  // action FROM a specific Tasks row (Log call), the URL carries
  // origin_task_id (Customer360Page.jsx forwards it unchanged); threading
  // it through to POST /calls/log lets the backend complete THAT EXACT
  // task, the strongest correlation available, instead of falling back to
  // an (opportunity, task_type) match.
  const [taskLaunchParams] = useSearchParams();
  const originTaskId = taskLaunchParams.get("origin_task_id") || null;
  const returnToTasksUrl = originTaskId && taskLaunchParams.get("return_to")
    ? safeInternalPath(decodeURIComponent(taskLaunchParams.get("return_to"))) : null;
  const dialogNavigate = useNavigate();
  // 2026-09 P4 Round 2 (Problem 1, item 7) — after a task-launched call is
  // logged, offer one clearly-presented way back to the exact Tasks URL
  // the employee came from (never a forced auto-redirect away from
  // Customer 360, which they may still want to look at) — the task
  // itself is already auto-completed server-side by this point.
  const offerReturnToTasks = () => {
    if (!returnToTasksUrl) return;
    toast.message("Task completed", {
      description: "The originating task was completed automatically.",
      action: { label: "Return to Tasks", onClick: () => dialogNavigate(returnToTasksUrl) },
    });
  };
  const [policy, setPolicy] = useState(null);
  const [connected, setConnected] = useState(true);
  const [disposition, setDisposition] = useState("");
  const [note, setNote] = useState("");
  const [intent, setIntent] = useState("");
  const [connectedOutcome, setConnectedOutcome] = useState("");
  const [followup, setFollowup] = useState("");
  const [saving, setSaving] = useState(false);

  // Book-consultation sub-flow — the best possible outcome of a call.
  const [productCode, setProductCode] = useState(row?.product_code || null);
  const [bookOpen, setBookOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [date, setDate] = useState("");
  const [booking, setBooking] = useState(false);
  const bookingGuard = useRef(false);        // a second click / Enter while a booking is in flight must not post a second booking
  const [counsellorUserId, setCounsellorUserId] = useState(user?.user_id || "");
  const [counsellors, setCounsellors] = useState([]);
  // R15 U1: the slot belongs to the exact (type, date, product, counsellor) it was fetched for - changing any of them invalidates it immediately; late responses are ignored;
  // the status says why nothing can be picked (closed day / not permitted / could not load).
  const picker = useSlotPicker({
    key: bookOpen && typeId && date ? `${typeId}|${date}|${productCode || ""}|${counsellorUserId || ""}|${row?.opportunity_id || ""}` : null, date,
    load: async () => (await api.get(`/appointments/slots?appointment_type_id=${typeId}&date_str=${date}`)).data,
  });

  // The reset below is a "dialog opened / a different row was chosen" event,
  // NOT something to re-run whenever the auth context re-renders. The signed-in
  // user's id is only read as the default counsellor at that moment, so it is
  // read through a ref: listing `user?.user_id` as a dependency would wipe a
  // half-typed call log (and re-fetch) if the session object were ever
  // refreshed while the dialog is open.
  const userIdRef = useRef(user?.user_id);
  userIdRef.current = user?.user_id;

  useEffect(() => {
    if (!open) return undefined;
    // A slow response for a previously opened row must not overwrite the
    // state of the row that is open now (or of a closed dialog).
    let stale = false;
    api.get("/calls/policy").then((r) => { if (!stale) setPolicy(r.data); });
    setConnected(true); setDisposition(""); setNote(""); setIntent(""); setConnectedOutcome(""); setFollowup("");
    setBookOpen(false); setTypeId(""); setDate("");
    setCounsellorUserId(userIdRef.current || "");
    if (row?.product_code) {
      setProductCode(row.product_code);
    } else if (row?.opportunity_id) {
      api.get(`/opportunities?opportunity_id=${row.opportunity_id}`)
        .then((r) => { if (!stale) setProductCode(r.data?.[0]?.product_code || null); })
        .catch(() => { if (!stale) setProductCode(null); });
    }
    return () => { stale = true; };
  }, [open, row]);

  useEffect(() => {
    if (!bookOpen) return;
    api.get("/appointments/types").then((r) => {
      setTypes(r.data.filter((t) => t.active));
    });
    // Canonical resolution (backend, not a second React copy of the same
    // rule) — falls back to the product's family/mother appointment type
    // when a commercial child product (SM_INHOUSE/SM_MARA/SA_PREMIUM/
    // SA_PARTNERSHIP) has none of its own.
    if (productCode) {
      api.get(`/appointments/resolve-type?product_code=${productCode}`).then((r) => {
        if (r.data.appointment_type) setTypeId(r.data.appointment_type.appointment_type_id);
      });
    }
    if (hasCap("booking.assign")) {
      api.get("/users").then((r) => setCounsellors(
        r.data.users.filter((u) => u.is_active && ["counsellor", "supervisor", "manager", "admin"].includes(u.role))
      )).catch(() => {});
    }
  }, [bookOpen, productCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/calls/log", {
        contact_id: row.contact_id, opportunity_id: row.opportunity_id,
        connected, disposition: connected ? null : disposition,
        note: note || null, intent: intent || null,
        connected_outcome: connected ? (connectedOutcome || null) : null,
        origin_task_id: originTaskId,
        followup_at: followup ? new Date(followup).toISOString() : null,
      });
      toast.success(data.followups_scheduled?.length
        ? `Call logged — ${data.followups_scheduled.length} follow-up(s) scheduled`
        : "Call logged");
      if (data.auto_claimed) toast.info("You're now the owner of this lead");
      if (data.claim_released) toast.info("Lead released back to the calling pool");
      offerReturnToTasks();
      onLogged?.();
      setOpen(false);
      // 2026-09 Workstream J Section 5 — same cross-component refresh as the
      // consultation outcome save (see ConsultationActions.jsx).
      notifyTaskReviewChanged();
      maybeShowTaskReview(data.task_review);
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  const bookAndLog = async () => {
    if (!picker.slot || bookingGuard.current) return;
    bookingGuard.current = true;
    setBooking(true);
    try {
      // The rep booking this consultation is right here, on the call — default
      // to them as counsellor rather than leaving it unassigned (unlike
      // self-booking / voice-agent bookings, which have no human to assign
      // yet and correctly stay unassigned for pickup elsewhere). Staff with
      // booking.assign can override this to hand it to someone else instead.
      await api.post("/appointments", { opportunity_id: row.opportunity_id, appointment_type_id: typeId, start: picker.slot, counsellor_user_id: counsellorUserId || user?.user_id || null, origin_task_id: originTaskId });
    } catch (e) {
      toast.error(errDetail(e));
      setBooking(false);
      bookingGuard.current = false;
      picker.reload();            // the server re-checked the slot / seat and refused: show what is really available now
      return;
    }
    let logResult;
    try {
      const { data } = await api.post("/calls/log", {
        contact_id: row.contact_id, opportunity_id: row.opportunity_id,
        connected: true, disposition: null,
        note: note || "Consultation booked during call",
        intent: intent || "high_intent",
        connected_outcome: "consultation_booked",
        origin_task_id: originTaskId,
        followup_at: null,
      });
      logResult = data;
    } catch (e) {
      // Booking already succeeded — the call-log write is bookkeeping on top
      // of it, so surface the failure but don't imply the booking failed too.
      toast.warning(`Consultation booked, but the call log couldn't be saved: ${errDetail(e)}`);
      onLogged?.();
      setOpen(false);
      setBooking(false);
      bookingGuard.current = false;
      return;
    }
    if (logResult?.auto_claimed) toast.info("You're now the owner of this lead");
    toast.success("Consultation booked — call logged and moved off your calling queue");
    offerReturnToTasks();
    onLogged?.();
    setOpen(false);
    setBooking(false);
    bookingGuard.current = false;
    notifyTaskReviewChanged();
    maybeShowTaskReview(logResult?.task_review);
  };

  const claimPending = row?.owner_claim_status === "pending_outcome";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && claimPending) return; setOpen(o); }}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" hideClose={claimPending}>
        <DialogHeader>
          <DialogTitle className="font-heading">Log call — {row?.contact_name}</DialogTitle>
          {claimPending && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              You've claimed this lead — select an outcome to continue. This can't be dismissed without logging the call.
            </p>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Label className="text-sm">Connected?</Label>
            <Switch data-testid="call-connected-toggle" checked={connected} onCheckedChange={setConnected} />
            <span className="text-xs text-muted-foreground">{connected ? "Spoke with customer" : "Not connected — pick a disposition"}</span>
          </div>
          {!connected && (
            <div className="space-y-1.5">
              <Label className="text-xs">Disposition (required)</Label>
              <Select value={disposition} onValueChange={setDisposition}>
                <SelectTrigger data-testid="call-disposition-select" className="h-9"><SelectValue placeholder="Why did it fail?" /></SelectTrigger>
                <SelectContent>
                  {(policy?.dispositions || []).map((d) => (
                    <SelectItem key={d.key} value={d.key}>{d.label}{d.terminal ? " (no retries)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {connected && (
            <div className="space-y-1.5">
              <Label className="text-xs">Intent (required for connected calls)</Label>
              <Select value={intent} onValueChange={setIntent}>
                <SelectTrigger data-testid="call-intent-select" className="h-9"><SelectValue placeholder="Customer intent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high_intent">High intent</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="cold">Cold</SelectItem>
                  <SelectItem value="not_interested">Not interested</SelectItem>
                  <SelectItem value="not_eligible">Not eligible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {connected && row?.opportunity_id && (
            <div className="space-y-1.5">
              <Label className="text-xs">Follow-up status (required)</Label>
              <Select value={connectedOutcome} onValueChange={setConnectedOutcome}>
                <SelectTrigger data-testid="call-connected-outcome-select" className="h-9"><SelectValue placeholder="What happens next?" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resolved">Resolved — no further follow-up needed</SelectItem>
                  <SelectItem value="callback_required">Callback / further follow-up required</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Booking a consultation below counts as resolved automatically.</p>
            </div>
          )}
          {row?.opportunity_id && (
            <div className="space-y-1.5">
              <Label className="text-xs">Tags</Label>
              <TagsEditor entity="opportunity" recordId={row.opportunity_id} tags={row.tags || []} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Note {connected ? "(required)" : "(required only for 'Other')"}</Label>
            <Textarea data-testid="call-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What was discussed / observed…" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Schedule follow-up (optional — else policy auto-schedules retries)</Label>
            <Input data-testid="call-followup-input" type="datetime-local" className="h-9"
              value={followup} onChange={(e) => setFollowup(e.target.value)} />
          </div>
          <div className="rounded-md border p-3 space-y-2.5" data-testid="call-book-section">
            {!bookOpen ? (
              <>
                <Button data-testid="call-book-open" size="sm" variant="outline" className="w-full"
                  disabled={!connected || !row?.opportunity_id} onClick={() => setBookOpen(true)}>
                  <CalendarClock className="h-3.5 w-3.5 mr-1.5" /> Book consultation
                </Button>
                {!row?.opportunity_id && <p className="text-[11px] text-muted-foreground">No linked opportunity — booking isn't available for this call.</p>}
                {row?.opportunity_id && !connected && <p className="text-[11px] text-muted-foreground">Only available when the call connected.</p>}
              </>
            ) : (
              <>
                <Label className="text-xs font-medium">Book consultation</Label>
                <div className="space-y-1.5">
                  <Label className="text-xs">Appointment type</Label>
                  <Select value={typeId} onValueChange={setTypeId}>
                    <SelectTrigger data-testid="call-book-type" className="h-9"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => <SelectItem key={t.appointment_type_id} value={t.appointment_type_id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Date</Label>
                  <Input data-testid="call-book-date" type="date" className="h-9" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                {hasCap("booking.assign") && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Counsellor (defaults to you)</Label>
                    <Select value={counsellorUserId} onValueChange={setCounsellorUserId}>
                      <SelectTrigger data-testid="call-book-counsellor" className="h-9"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={user?.user_id}>{user?.name} (you)</SelectItem>
                        {counsellors.filter((c) => c.user_id !== user?.user_id).map((c) => (
                          <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {picker.status !== "idle" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Available slots (IST)</Label>
                    <SlotPickerView picker={picker} idPrefix="call-book" compact gridClass="grid grid-cols-4 gap-1.5" />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button data-testid="call-book-cancel" size="sm" variant="ghost" onClick={() => setBookOpen(false)}>Cancel</Button>
                  <Button data-testid="call-book-confirm" size="sm" className="flex-1" disabled={!picker.slot || !typeId || booking} onClick={bookAndLog}>
                    {booking ? "Booking…" : "Confirm & book"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="call-log-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Log call"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RowWithDivider = ({ row, showDivider, children }) => (
  <>
    {showDivider && (
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={11} className="py-1.5 text-[11px] font-medium text-muted-foreground bg-muted/40">
          New leads received today — pick these up after your own follow-ups
        </TableCell>
      </TableRow>
    )}
    <TableRow data-testid={`calls-row-${row.opportunity_id}`}>{children}</TableRow>
  </>
);

// 2026-09 P3-C — Call Activity: individual call ATTEMPTS, keyed on the
// real caller (GET /calls/activity, backed by db.interactions directly —
// never one-latest-row-per-customer like the Assigned Leads workqueue
// above). Deliberately its own component/state, never merged with the
// Assigned Leads query via an OR.
const CallActivityView = ({ canTeamView, currentUserId }) => {
  // 2026-09 P4 Round 2 (Problem 5) — every Call Activity filter (outcome,
  // date range, team scope, caller, pagination) lives in the URL via
  // useUrlState, not plain component state, so it survives a customer
  // visit + Back exactly like the tab selection already does.
  const [activityState, setActivityState] = useUrlState({
    connected: "all", date_from: "", date_to: "", scope: "own", caller: "all", page: 1,
  });
  const { connected, date_from: dateFrom, date_to: dateTo, scope, caller: callerUserId, page } = activityState;
  const setConnected = (v) => setActivityState({ connected: v, page: 1 });
  const setDateFrom = (v) => setActivityState({ date_from: v, page: 1 });
  const setDateTo = (v) => setActivityState({ date_to: v, page: 1 });
  const setScope = (v) => setActivityState({ scope: v, caller: "all", page: 1 });
  const setCallerUserId = (v) => setActivityState({ caller: v, page: 1 });
  const setPage = (v) => setActivityState({ page: v });
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(null);
  const limit = 25;
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);

  useEffect(() => {
    if (canTeamView) api.get("/users").then((r) => setEmployees((r.data.users || []).filter((u) => u.is_active))).catch(() => {});
  }, [canTeamView]);

  const load = useCallback(() => {
    setData(null);
    const params = new URLSearchParams({ connected, page: String(page), limit: String(limit) });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (canTeamView && scope === "team") {
      params.set("scope", "team");
      if (callerUserId !== "all") params.set("caller_user_id", callerUserId);
    }
    api.get(`/calls/activity?${params.toString()}`).then((r) => setData(r.data));
  }, [connected, dateFrom, dateTo, scope, callerUserId, page, canTeamView]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.items || [];

  return (
    <div className="space-y-3" data-testid="call-activity-view">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={connected} onValueChange={setConnected}>
          <SelectTrigger data-testid="activity-connected-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All calls</SelectItem>
            <SelectItem value="connected">Connected</SelectItem>
            <SelectItem value="not_connected">Not Connected</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground">Call date</Label>
          <Input data-testid="activity-date-from" type="date" className="h-9 w-36 text-xs"
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <span className="text-xs text-muted-foreground">to</span>
          <Input data-testid="activity-date-to" type="date" className="h-9 w-36 text-xs"
            value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        {canTeamView && (
          <>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="activity-scope-select" className="h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="own">My calls only</SelectItem>
                <SelectItem value="team">Team</SelectItem>
              </SelectContent>
            </Select>
            {scope === "team" && (
              <Select value={callerUserId} onValueChange={setCallerUserId}>
                <SelectTrigger data-testid="activity-caller-filter" className="h-9 w-44 text-xs"><SelectValue placeholder="Caller…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Every caller</SelectItem>
                  {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </>
        )}
      </div>
      <Card>
        {!data ? (
          <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={PhoneCall} title="No calls in this range" testId="activity-empty" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Caller</TableHead>
                <TableHead>Call time (IST)</TableHead>
                <TableHead>Result</TableHead>
                <TableHead className="hidden lg:table-cell">Disposition</TableHead>
                <TableHead className="hidden lg:table-cell">Note</TableHead>
                <TableHead className="hidden lg:table-cell">Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.interaction_id} data-testid={`activity-row-${r.interaction_id}`}>
                  <TableCell>
                    <Link to={`/customers/${r.contact_id}?return_to=${returnTo}`} className="font-medium hover:underline">{r.contact_name}</Link>
                    <TestBadge show={r.is_test_record} />
                    <br /><span className="text-[11px] font-mono text-muted-foreground">{r.customer_id}</span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.product_name || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid={`activity-caller-${r.interaction_id}`}>{r.caller_label}</TableCell>
                  <TableCell className="text-sm">{fmtIST(r.call_at)}</TableCell>
                  <TableCell>
                    <Badge variant={r.connected ? "default" : "outline"}
                      className={r.connected ? "" : "text-[hsl(var(--warning))] border-[hsl(var(--warning))]"}>
                      {r.connected ? "Connected" : "Not Connected"}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{r.disposition_label || r.disposition || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-56 truncate">{r.note || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{r.source || r.channel || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="activity-pagination">
          <span>{(page - 1) * limit + 1}–{(page - 1) * limit + rows.length} of {data.total}</span>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1}
              data-testid="activity-page-prev" onClick={() => setPage(Math.max(1, page - 1))}>Previous</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!data.has_more}
              data-testid="activity-page-next" onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CallsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);
  const { user, hasCap } = useAuth();
  // 2026-09 P3-C — Assigned Leads (existing queue-based workload, keyed on
  // opportunity ownership) vs. Call Activity (individual call attempts,
  // keyed on the real caller) are deliberately separate views, never
  // combined with an OR query. See routers_platform.call_activity's own
  // docstring for why.
  // 2026-09 P4 (Problem 10) — view/queue live in the URL (useUrlState),
  // not plain component state, so navigating to a customer from here and
  // using the browser's Back button restores the exact tab that was open,
  // instead of always resetting to "Assigned/My".
  // 2026-09 P4 Round 2 (Problem 5) — every Assigned Leads filter (product,
  // lead-received window, tag, Leadership scope + scoped employee), not
  // only the view/queue tabs, now lives in the URL.
  const [callsUrlState, setCallsUrlState] = useUrlState({
    view: "assigned", queue: "my", product: "all", received: "all", tag: "all",
    scope: "own", scope_employee: "all",
  });
  const { view, queue, product, received: receivedDays, tag,
    scope, scope_employee: scopeEmployee } = callsUrlState;
  const setView = (v) => setCallsUrlState({ view: v });
  const setQueue = (q) => setCallsUrlState({ queue: q });
  const setProduct = (v) => setCallsUrlState({ product: v });
  const setReceivedDays = (v) => setCallsUrlState({ received: v });
  const setTag = (v) => setCallsUrlState({ tag: v });
  const setScope = (v) => setCallsUrlState({ scope: v });
  const setScopeEmployee = (v) => setCallsUrlState({ scope_employee: v });
  const [products, setProducts] = useState([]);
  const [tags, setTags] = useState([]);
  const [rows, setRows] = useState(null);
  // 2026-09 Section 5 fix — a failed /calls/queues request (e.g. Team Queue's
  // company-wide scan hitting one malformed record) must show an honest
  // error + Retry, never leave `rows` stuck at null forever (an endless
  // loading skeleton that looks identical to "broken"/blank — the previously
  // reported Team Queue symptom). Cleared on every new load attempt.
  const [rowsError, setRowsError] = useState(false);
  const [logRow, setLogRow] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [waRow, setWaRow] = useState(null);
  const [lostRow, setLostRow] = useState(null);
  const [lostReason, setLostReason] = useState("");
  const [lostInitiatedBy, setLostInitiatedBy] = useState("");
  const canAssign = hasCap("crm.assign");
  const [employees, setEmployees] = useState([]);
  const [takeOverRow, setTakeOverRow] = useState(null);
  const [takeOverUser, setTakeOverUser] = useState("");
  const [takeOverBusy, setTakeOverBusy] = useState(false);
  // Leadership scope (own/team/company) for the scope-eligible queues —
  // same reports.team/reports.company gate the backend already enforces.
  const canScopeTeam = hasCap("reports.team") || hasCap("reports.company");
  // 2026-09 round 8 — a stale/shared ?queue=team link opened by someone
  // without the capability falls back to "my" instead of hitting the
  // backend's 403 (the tab itself is already hidden above; this covers a
  // direct URL, not just the tab click).
  useEffect(() => {
    if (queue === "team" && !canScopeTeam) setQueue("my");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, canScopeTeam]);

  // Unowned rows (new/unclaimed leads) are visible identically to every
  // caller viewing this queue at once — if several people click "Call" on
  // the same top row within seconds of each other, they'd all dial the
  // same person before any of them logs an outcome. Claim atomically FIRST
  // (same race-safe find_one_and_update the Leads-page Claim button uses —
  // exactly one caller wins), so only one person ever picks up the phone.
  // Already-owned rows (your own follow-ups, or Team Queue visibility into
  // a teammate's lead) skip straight to logging — nothing to claim.
  const startCall = async (r) => {
    // Same safe pattern as Leads (spec Section 3): don't let a manual call
    // look like the expected next step while AI Voice genuinely owns
    // outreach. Warn first; "Take over from AI" is the deliberate interrupt.
    if (r.voice_state?.owns_outreach
        && !window.confirm("AI Voice is currently handling this lead (" + r.voice_state.label
          + "). Placing a manual call now does NOT stop the AI — use “Take over from AI” "
          + "for that. Call anyway?")) {
      return;
    }
    if (r.owner_user_id) { setLogRow(r); return; }
    setClaimingId(r.opportunity_id);
    try {
      await api.post(`/leads/claim/${r.opportunity_id}`);
      setLogRow({ ...r, owner_user_id: user?.user_id, owner_name: user?.name,
                 owner_claim_status: "pending_outcome" });
      load();
    } catch (e) {
      toast.error(errDetail(e, "This lead was just claimed by someone else"));
      load();
    } finally {
      setClaimingId(null);
    }
  };

  const markLost = async () => {
    try {
      // NOTE: this previously called PATCH /opportunities/{id}/status, a
      // URL with no matching backend route — every "Mark Lost" click from
      // Calls has been silently failing (404) in production. The real
      // canonical endpoint is POST .../lifecycle (routers_crm.change_lifecycle).
      await api.post(`/opportunities/${lostRow.opportunity_id}/lifecycle`,
        { status: "closed_lost", reason: lostReason, closure_initiated_by: lostInitiatedBy });
      toast.success("Marked Lost — history and analytics preserved");
      setLostRow(null); setLostReason(""); setLostInitiatedBy(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const submitTakeOver = async () => {
    if (!takeOverRow || !takeOverUser) return;
    setTakeOverBusy(true);
    try {
      const { data } = await api.post(`/leads/${takeOverRow.opportunity_id}/take-over-from-ai`,
        { owner_user_id: takeOverUser, expected_current_owner_user_id: takeOverRow.owner_user_id || null });
      toast.success(`Taken over from AI — assigned to ${data.owner}`
        + (data.lease_released ? " · Voice slot released" : "")
        + (data.jobs_cancelled ? ` · ${data.jobs_cancelled} pending AI job(s) cancelled` : ""));
      setTakeOverRow(null); setTakeOverUser(""); load();
    } catch (e) {
      toast.error(errDetail(e, "Could not take over"));
      load();
    } finally {
      setTakeOverBusy(false);
    }
  };

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  useEffect(() => {
    if (canAssign || canScopeTeam) api.get("/users").then((r) => setEmployees((r.data.users || []).filter((u) => u.is_active))).catch(() => {});
  }, [canAssign, canScopeTeam]);
  useEffect(() => { api.get("/tags").then((r) => setTags(r.data.tags || [])).catch(() => {}); }, []);
  const scopeEligible = SCOPE_ELIGIBLE_QUEUES.includes(queue);
  const load = useCallback(() => {
    setRows(null);
    setRowsError(false);
    const params = new URLSearchParams({ queue });
    if (product !== "all") params.set("product_code", product);
    if (receivedDays !== "all") params.set("received_days", receivedDays);
    if (tag !== "all") params.set("tag", tag);
    if (scopeEligible && canScopeTeam && scope !== "own") {
      params.set("scope", scope);
      if (scopeEmployee !== "all") params.set("owner_user_id", scopeEmployee);
    }
    api.get(`/calls/queues?${params.toString()}`)
      .then((r) => setRows(r.data))
      .catch(() => setRowsError(true));
  }, [queue, product, receivedDays, tag, scope, scopeEmployee, scopeEligible, canScopeTeam]);
  useEffect(() => { load(); }, [load]);
  // Scope/employee controls only make sense on scope-eligible queues —
  // reset back to "own" when navigating away so a stale scope doesn't
  // silently apply if the user tabs back into one later.
  useEffect(() => { if (!scopeEligible) { setCallsUrlState({ scope: "own", scope_employee: "all" }); } }, [scopeEligible]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="calls-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Calls</h1>
          <p className="text-sm text-muted-foreground">
            {view === "assigned"
              ? "Assigned Leads — lead/opportunity workload by RESPONSIBILITY (owner). \"Employee\" filters and \"Received\" dates here are about the lead owner and when the lead arrived, not who called or when."
              : "Call Activity — every individual call attempt, by who actually placed it. Ownership is never changed here."}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 rounded-md border p-1">
            <Button size="sm" variant={view === "assigned" ? "default" : "ghost"} className="h-7 text-xs"
              data-testid="calls-view-assigned" onClick={() => setView("assigned")}>Assigned Leads</Button>
            <Button size="sm" variant={view === "activity" ? "default" : "ghost"} className="h-7 text-xs"
              data-testid="calls-view-activity" onClick={() => setView("activity")}>Call Activity</Button>
          </div>
          {view === "assigned" && hasCap("crm.create") && <OnboardingWizardDialog onCreated={load} />}
        </div>
      </div>
      {view === "activity" ? (
        <CallActivityView canTeamView={hasCap("reports.team") || hasCap("reports.company")}
          currentUserId={user?.user_id} />
      ) : (
      <>
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={receivedDays} onValueChange={setReceivedDays}>
          <SelectTrigger data-testid="calls-received-filter" className="h-9 w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any time (lead received)</SelectItem>
            <SelectItem value="today">Lead received today</SelectItem>
            <SelectItem value="7">Lead received — last 7 days</SelectItem>
            <SelectItem value="30">Lead received — last 30 days</SelectItem>
            <SelectItem value="90">Lead received — last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger data-testid="calls-tag-filter" className="h-9 w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {tags.map((t) => <SelectItem key={t.tag_id} value={t.tag_id}>{t.name} ({t.group})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={product} onValueChange={setProduct}>
          <SelectTrigger data-testid="calls-product-filter" className="h-9 w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Tabs value={queue} onValueChange={setQueue}>
        <TabsList className="flex-wrap h-auto">
          {/* 2026-09 round 8 — Team Queue is company-wide (every employee's
              assigned leads), gated on the same reports.team/reports.company
              tier the backend enforces; an ordinary counsellor never even
              sees the tab, rather than clicking into a 403. */}
          {QUEUES.filter((q) => q.key !== "team" || canScopeTeam).map((q) => (
            <TabsTrigger key={q.key} data-testid={`calls-queue-${q.key}`} value={q.key}>{q.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {scopeEligible && canScopeTeam && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={scope} onValueChange={(v) => setCallsUrlState({ scope: v, scope_employee: "all" })}>
            <SelectTrigger data-testid="calls-scope-select" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="own">Own</SelectItem>
              {hasCap("reports.team") && <SelectItem value="team">Team</SelectItem>}
              {hasCap("reports.company") && <SelectItem value="company">All</SelectItem>}
            </SelectContent>
          </Select>
          {scope !== "own" && (
            <Select value={scopeEmployee} onValueChange={setScopeEmployee}>
              <SelectTrigger data-testid="calls-scope-employee" className="h-8 w-48 text-xs"><SelectValue placeholder="Lead owner…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every lead owner</SelectItem>
                {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
      <Card>
        {!rows ? (
          rowsError ? (
            <ErrorState testId="calls-queue-error" title="Couldn't load this queue" onRetry={load} />
          ) : (
            <ListSkeleton rows={3} testId="calls-queue-loading" />
          )
        ) : rows.length === 0 ? (
          <EmptyState icon={PhoneCall} title="No calls in this queue" hint="Queues fill automatically from lead flow, follow-up tasks and call outcomes." testId="calls-empty" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead>Product / Stage</TableHead>
                <TableHead>AI Voice</TableHead>
                <TableHead className="hidden lg:table-cell">Source</TableHead>
                <TableHead className="hidden lg:table-cell">Received</TableHead>
                <TableHead className="hidden lg:table-cell">Attempts</TableHead>
                <TableHead className="hidden lg:table-cell">Last outcome</TableHead>
                <TableHead className="hidden md:table-cell">Intent</TableHead>
                <TableHead className="hidden lg:table-cell">Next follow-up</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <RowWithDivider key={r.opportunity_id} row={r} showDivider={queue === "followups_today" && r.is_new_lead && !rows[i - 1]?.is_new_lead}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Link to={`/customers/${r.contact_id}?return_to=${returnTo}`} className="font-medium hover:underline">{r.contact_name}</Link>
                      <TestBadge show={r.is_test_record} />
                      {r.is_new_lead && <Badge variant="outline" className="text-[9px] border-[hsl(var(--info))] text-[hsl(var(--info))]">New</Badge>}
                    </div>
                    <span className="text-[11px] font-mono text-muted-foreground">{r.customer_id}</span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm font-mono">{r.phone || "—"}</TableCell>
                  <TableCell className="text-sm">{r.product_name}<br /><span className="text-xs text-muted-foreground">{r.stage_name}</span></TableCell>
                  <TableCell>{r.voice_state ? <VoiceStateBadge vs={r.voice_state} showOwnership /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{r.source || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{r.created_at ? fmtIST(r.created_at) : "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm">{r.attempts}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-40 truncate">{r.last_outcome || "Never called"}{r.last_note ? ` · ${r.last_note}` : ""}</TableCell>
                  <TableCell className="hidden md:table-cell"><IntentBadge intent={r.intent ? { category: r.intent } : null} /></TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{r.next_followup_at ? fmtIST(r.next_followup_at) : "—"}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1.5 flex-wrap">
                      {canAssign && r.voice_state?.takeover_available && (
                        <Button data-testid={`calls-takeover-${r.opportunity_id}`} size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => { setTakeOverRow(r); setTakeOverUser(""); }}>
                          Take over from AI
                        </Button>
                      )}
                      <Button data-testid={`calls-log-${r.opportunity_id}`} size="sm" className="h-7 text-xs"
                        disabled={claimingId === r.opportunity_id} onClick={() => startCall(r)}>
                        <PhoneCall className="h-3 w-3 mr-1" />
                        {claimingId === r.opportunity_id ? "Claiming…" : !r.owner_user_id ? "Claim & Call" : "Call"}
                      </Button>
                      <Button data-testid={`calls-wa-${r.opportunity_id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => setWaRow(r)}>
                        <WhatsAppIcon className="h-3.5 w-3.5 text-[#25D366]" />
                      </Button>
                      <Button data-testid={`calls-open-${r.opportunity_id}`} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(`/customers/${r.contact_id}?return_to=${returnTo}`)}>
                        <UserSearch className="h-3 w-3" />
                      </Button>
                      {queue !== "lost" && (
                        <Button data-testid={`calls-lost-${r.opportunity_id}`} size="sm" variant="ghost" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => { setLostRow(r); setLostReason(""); }}>
                          Lost
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </RowWithDivider>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      </>
      )}
      {logRow && <CallLogDialog row={logRow} open={!!logRow} setOpen={(o) => !o && setLogRow(null)} onLogged={load} />}
      <Dialog open={!!takeOverRow} onOpenChange={(o) => { if (!o) { setTakeOverRow(null); setTakeOverUser(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="calls-takeover-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Take over from AI Voice</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            AI Voice is currently handling <b>{takeOverRow?.contact_name}</b>
            {takeOverRow?.voice_state?.label ? ` (${takeOverRow.voice_state.label})` : ""}.
            This safely stops any pending AI attempt, frees the Voice slot if held, keeps the
            full Voice history, and hands the customer to the person you pick.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Hand over to</Label>
            <Select value={takeOverUser} onValueChange={setTakeOverUser}>
              <SelectTrigger data-testid="calls-takeover-target"><SelectValue placeholder="Pick employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button data-testid="calls-takeover-confirm" disabled={!takeOverUser || takeOverBusy} onClick={submitTakeOver}>
              {takeOverBusy ? "Taking over…" : "Take over from AI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {waRow && <QuickWhatsAppDialog contact={{ contact_id: waRow.contact_id, full_name: waRow.contact_name }}
        open={!!waRow} setOpen={(o) => !o && setWaRow(null)} />}
      {/* Mandatory Lost confirmation with final WhatsApp option */}
      <Dialog open={!!lostRow} onOpenChange={(o) => !o && (setLostRow(null), setLostReason(""), setLostInitiatedBy(""))}>
        <DialogContent className="sm:max-w-md" data-testid="lost-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading">Mark Lost — {lostRow?.contact_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This closes the opportunity as Lost. Attempts: {lostRow?.attempts ?? 0}. History and No-Contact analytics are preserved. You can send one final WhatsApp message before completing.</p>
            {lostRow?.phone && (
              <a href={`https://wa.me/${String(lostRow.phone).replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="lost-final-whatsapp">
                  <WhatsAppIcon className="h-3.5 w-3.5 mr-1 text-[#25D366]" /> Send final WhatsApp message
                </Button>
              </a>
            )}
            <Textarea data-testid="lost-reason" placeholder="Lost reason / remarks (required)" value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Who decided not to proceed? (required)</label>
              <Select value={lostInitiatedBy} onValueChange={setLostInitiatedBy}>
                <SelectTrigger data-testid="lost-initiated-by" className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer decided not to proceed</SelectItem>
                  <SelectItem value="aussie_pathways">Aussie Pathways declined this customer</SelectItem>
                  <SelectItem value="mutual_other">Mutual / other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="lost-confirm" variant="destructive" disabled={!lostReason.trim() || !lostInitiatedBy} onClick={markLost}>Confirm Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
